import { useMemo } from 'react';
import { ShieldUser } from 'lucide-react';
import { getActiveDrillItemPath, getActiveNavItemLabel } from '../NavBar/nav-utils';
import {
  IdentityHeader,
  IdentityTile,
  TakeoverAside,
  TakeoverNavGroups,
  TakeoverSearchField,
  takeoverShellClassName,
  useTakeoverNavSearch,
} from '../TakeoverShell';
import type { NavItem } from '../NavBar/nav-types';

export interface AdminNavGroup {
  label: string;
  items: NavItem[];
}

export interface AdminShellViewProps {
  groups: AdminNavGroup[];
  /** Current pathname, used to highlight the active nav entry. */
  activePath: string;
  onBack: () => void;
  children: React.ReactNode;
}

/**
 * Presentational layout of the Admin takeover: a dedicated sidebar beside the
 * admin page content. Back chip, identity header, search, and grouped nav
 * rows. `esc` mirrors the chip: it exits Admin in one press. The connected
 * {@link AdminShell} wires nav sources, routing, and the escape/back behavior;
 * stories drive this view directly.
 */
export function AdminShellView({ groups, activePath, onBack, children }: AdminShellViewProps) {
  const { query, setQuery, filterGroups } = useTakeoverNavSearch();

  const navItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const activeItemPath = useMemo(
    () => getActiveDrillItemPath(navItems, activePath),
    [navItems, activePath],
  );
  const pageTitle = getActiveNavItemLabel(navItems, activePath, 'Admin');

  return (
    <div className={takeoverShellClassName} data-testid="admin-shell">
      <TakeoverAside
        back={{ label: 'Back', onClick: onBack, testId: 'admin-back' }}
        header={
          <IdentityHeader
            tile={<IdentityTile icon={ShieldUser} />}
            title="Admin"
            subtitle="Instance administration"
          />
        }
        pageTitle={pageTitle}
        page={<div className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</div>}
      >
        <TakeoverSearchField query={query} setQuery={setQuery} placeholder="Search admin" />
        <nav
          aria-label="Admin navigation"
          className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-1.5 pb-3 scrollbar-fade"
        >
          <TakeoverNavGroups
            groups={filterGroups(groups)}
            activeItemPath={activeItemPath}
            testIdPrefix="admin-nav"
          />
        </nav>
      </TakeoverAside>
    </div>
  );
}
